import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/api";
import { API_ENDPOINTS } from "../constants/actionTypes";

// Thunk for fetching ALL branch settings
export const fetchAllBranchSettings = createAsyncThunk(
  "branchSetting/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.BRANCH_SETTING.SELECT_ALL,
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Branch Settings API Error:",
        error.response?.status,
        error.response?.data,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch branch settings",
      );
    }
  },
);

// Thunk for adding a new branch setting
export const addBranchSetting = createAsyncThunk(
  "branchSetting/add",
  async (settingData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.BRANCH_SETTING.INSERT,
        settingData,
      );
      dispatch(fetchAllBranchSettings());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add branch setting",
      );
    }
  },
);

// Thunk for updating an existing branch setting
export const updateBranchSetting = createAsyncThunk(
  "branchSetting/update",
  async (settingData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.BRANCH_SETTING.UPDATE,
        settingData,
      );
      dispatch(fetchAllBranchSettings()); // Refresh list after update
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update branch setting",
      );
    }
  },
);

// Thunk for generating QR code for a branch
export const generateBranchQR = createAsyncThunk(
  "branchSetting/generateQR",
  async (branchId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${API_ENDPOINTS.BRANCH_SETTING.GENERATE_QR}?branchId=${branchId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to generate QR code",
      );
    }
  },
);

// Thunk for downloading QR code for a branch
export const downloadBranchQR = createAsyncThunk(
  "branchSetting/downloadQR",
  async (branchId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.BRANCH_SETTING.DOWNLOAD_QR}?branchId=${branchId}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `BranchQR_${branchId}.pdf`); // Use branchId to make filename unique
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      return {};
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to download QR code",
      );
    }
  },
);

// Thunk for downloading ALL QR codes
export const downloadAllBranchQR = createAsyncThunk(
  "branchSetting/downloadAllQR",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.BRANCH_SETTING.DOWNLOAD_ALL_QR,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "AllBranchQRs.zip"); // Assuming zip format for multiple files
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      return {};
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to download all QR codes",
      );
    }
  },
);

const branchSettingSlice = createSlice({
  name: "branchSetting",
  initialState: {
    branchSettingsList: [], // Flat array for table
    pagination: null, // Full pagination wrapper
    isLoading: false,
    error: null,
  },
  reducers: {
    clearBranchSettingError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Branch Settings
      .addCase(fetchAllBranchSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllBranchSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.branchSettingsList = apiData?.data || apiData || [];
        state.pagination = apiData;
      })
      .addCase(fetchAllBranchSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch branch settings";
        console.error("❌ Branch Settings API Failed:", action.payload);
      })
      // Add Branch Setting
      .addCase(addBranchSetting.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addBranchSetting.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addBranchSetting.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Branch Setting
      .addCase(updateBranchSetting.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBranchSetting.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateBranchSetting.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Generate QR
      .addCase(generateBranchQR.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(generateBranchQR.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(generateBranchQR.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Download QR
      .addCase(downloadBranchQR.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(downloadBranchQR.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(downloadBranchQR.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Download All QR
      .addCase(downloadAllBranchQR.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(downloadAllBranchQR.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(downloadAllBranchQR.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearBranchSettingError } = branchSettingSlice.actions;
export default branchSettingSlice.reducer;
